import React from "react";
import { fetchQuery, graphql, type GraphQLTaggedNode } from "react-relay";

import { environment } from "~/models";

import useToggle from "./useToggle";

import type { useUsersBasicQuery } from "~/__generated__/useUsersBasicQuery.graphql";

enum UserShape {
	BASIC = 0,
}
graphql`
	fragment useUsers_basic_User on User {
		id
		type
		fullName
		firstName
		lastName
		email
		image
		isGuest
		isExternal # will always be false
		internalAttributes {
			invitable
		}
	}
`;

const userShapeQueries: { [k in UserShape]: GraphQLTaggedNode } = {
	[UserShape.BASIC]: graphql`
		query useUsersBasicQuery($first: Int, $search: String, $isGuests: Boolean) {
			viewer {
				users(first: $first, search: $search, isGuests: $isGuests) {
					edges {
						node {
							...useUsers_basic_User @relay(mask: false)
						}
					}
				}
			}
		}
	`,
};

type QueryResponse<T> = T extends UserShape.BASIC ? useUsersBasicQuery : never;

type UseUsersOptions = {
	perPage?: number;
	shape?: UserShape;
	ignoreEmptySearch?: boolean;
};
function useUsers<T extends UserShape>(
	searchTerm: string,
	{ perPage = 10, shape = UserShape.BASIC, ignoreEmptySearch = false }: UseUsersOptions,
): { data: QueryResponse<T>["response"] | undefined; loading: boolean; error: Error | undefined } {
	const [loading, toggleLoading] = useToggle(false);
	const [data, setData] = React.useState<QueryResponse<T>["response"] | undefined>(undefined);
	const [error, setError] = React.useState<Error | undefined>(undefined);
	React.useEffect(() => {
		if (ignoreEmptySearch && !searchTerm) {
			setData(() => undefined);
			setError(() => undefined);
			return;
		}
		toggleLoading();
		fetchQuery<QueryResponse<T>>(environment, userShapeQueries[shape], {
			search: searchTerm,
			first: perPage,
		})
			.toPromise()
			.then(data => {
				if (data?.viewer.users) {
					setData(data);
					setError(undefined);
				} else {
					throw new Error("");
				}
			})
			.catch(e => {
				setError(e);
				setData(undefined);
			})
			.finally(() => {
				toggleLoading();
			});
	}, [shape, setData, toggleLoading, setError, ignoreEmptySearch, searchTerm, perPage]);
	return { data, loading, error };
}

export default useUsers;
